import { Select } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { FC, useContext, useEffect, useMemo, useState } from 'react';

import getAddresses from '@/backend/graphql/user/getAddresses';
import { BookingFlowState } from '@/components/context/BookingFlowProvider';
import DatePicker from '@/components/DatePicker';
import {
  LTLOriginEndPointAccessorials,
  LTLDestinationEndPointAccessorials,
  FTLDestinationEndPointAccessorials,
  FTLOriginEndPointAccessorials,
  getCommonAccessorial,
} from '@/utils/Accessories';
import getPickupDates from '@/utils/getPickupDates';
import { ZipAddress } from '@/utils/types';
import { AddressWithAccessorial } from 'types';

import { GSAppContext } from '../context/GSAppContext';
import PostalCode from '../PostalCode';
import ValidatedField from '../ValidatedField';

import AdditionalServices from './AdditionalServices';
import LocationTypeSelect from './LocationTypeSelect';

export const summarizeAddress = (address: ZipAddress) => {
  if (!address) return undefined;
  const { city, state, postalCode, country } = address;
  return city && state && postalCode
    ? `${city}, ${state} ${postalCode}${country === 'CAN' ? ', CAN' : ''}`
    : undefined;
};

type EndpointFormProps = {
  endpointLabel: string;
  endpointField: string;
  includeDates?: Date[];
};
const EndpointForm: FC<EndpointFormProps> = ({
  endpointLabel,
  endpointField,
  children,
  includeDates,
}) => {
  const {
    setFieldValue,
    setFieldTouched,
    values: { rfq },
  } = useFormikContext<BookingFlowState>();

  const [addresses, setAddresses] = useState<AddressWithAccessorial[] | null>();
  const [originAddresses, setOriginAddresses] = useState<
    AddressWithAccessorial[]
  >();
  const [destinationAddresses, setDestinationAddresses] = useState<
    AddressWithAccessorial[]
  >();

  const {
    user: { user },
  } = useContext(GSAppContext);

  useEffect(() => {
    const fetchAddress = async () => {
      if (user) {
        const addresses = await getAddresses();
        setAddresses(addresses);
      }
    };
    if (!addresses) fetchAddress();
  }, [addresses, user]);

  useEffect(() => {
    const filteredOriginAddresses = addresses?.filter(
      address =>
        address.postalCode === rfq.origin.address.postalCode &&
        address.city === rfq.origin.address.city &&
        address.state === rfq.origin.address.state &&
        address.endpoint === 'ORIGIN' &&
        address.shipmentType === rfq.shipmentType,
    );
    setOriginAddresses(filteredOriginAddresses);
    if (filteredOriginAddresses?.length === 1) {
      const carrierAddedAccessorials = filteredOriginAddresses.at(0)
        ?.carrierAddedAccessorials;
      const useCount = filteredOriginAddresses.at(0)?.useCount || 0;
      if (carrierAddedAccessorials?.length && useCount > 1)
        setFieldValue(
          'rfq.origin.carrierAddedAccessorials',
          carrierAddedAccessorials,
        );
    }
    const filteredDestinationAddresses = addresses?.filter(
      address =>
        address.postalCode === rfq.destination.address.postalCode &&
        address.city === rfq.destination.address.city &&
        address.state === rfq.destination.address.state &&
        address.endpoint === 'DESTINATION' &&
        address.shipmentType === rfq.shipmentType,
    );
    setDestinationAddresses(filteredDestinationAddresses);
    if (filteredDestinationAddresses?.length === 1) {
      const carrierAddedAccessorials = filteredDestinationAddresses.at(0)
        ?.carrierAddedAccessorials;
      const useCount = filteredDestinationAddresses.at(0)?.useCount || 0;
      if (carrierAddedAccessorials?.length && useCount > 1)
        setFieldValue(
          'rfq.destination.carrierAddedAccessorials',
          carrierAddedAccessorials,
        );
    }
  }, [addresses, rfq.destination, rfq.origin, rfq.shipmentType]);

  const shipmentType = rfq.shipmentType;
  const isLTL = shipmentType !== 'FTL';

  const onSelectAddress = (
    address: AddressWithAccessorial,
    endpointLabel: string,
  ) => {
    if (endpointLabel === 'Pickup') {
      address.userSelectedAccessorials?.map(accessorial => {
        if (accessorial === 'IPU')
          setFieldValue('rfq.origin.accessorials.inside', true);
        if (accessorial === 'LFC')
          setFieldValue('rfq.origin.accessorials.liftgate', true);
        if (accessorial === 'PCH')
          setFieldValue('rfq.origin.accessorials.noFreeze', true);
      });
      setFieldValue('rfq.origin.address.streetAddress', address.streetAddress);
      setFieldValue('rfq.origin.type', address.addressType);
      setFieldValue(
        'rfq.origin.address.streetAddress1',
        address.streetAddress1,
      );
      if (address.carrierAddedAccessorials) {
        setFieldValue(
          'rfq.origin.carrierAddedAccessorials',
          address.carrierAddedAccessorials,
        );
      }
    }
    if (endpointLabel === 'Delivery') {
      address.userSelectedAccessorials?.map(accessorial => {
        if (accessorial === 'IDL')
          setFieldValue('rfq.destination.accessorials.inside', true);
        if (accessorial === 'LFC')
          setFieldValue('rfq.destination.accessorials.liftgate', true);
        if (accessorial === 'NDR')
          setFieldValue('rfq.destination.accessorials.appointment', true);
      });
      setFieldValue(
        'rfq.destination.address.streetAddress',
        address.streetAddress,
      );
      setFieldValue(
        'rfq.destination.address.streetAddress1',
        address.streetAddress1,
      );
      setFieldValue('rfq.destination.type', address.addressType);
      if (address.carrierAddedAccessorials) {
        setFieldValue(
          'rfq.destination.carrierAddedAccessorials',
          address.carrierAddedAccessorials,
        );
      }
    }
  };

  return (
    <div>
      <div className="md:flex mt-3 mb-2">
        <div className="md:w-48 md:mr-4">
          <ValidatedField
            name={`rfq.${endpointField}.address`}
            label={`${endpointLabel}  Zip Code*`}
            annotation={
              // @ts-expect-error TODO Couldn't get to some of these in
              // https://plslogistics.atlassian.net/browse/GS-736
              rfq[endpointField]?.address &&
              // @ts-expect-error TODO Couldn't get to some of these in
              // https://plslogistics.atlassian.net/browse/GS-736
              summarizeAddress(rfq[endpointField].address)
            }>
            {({ field }) => (
              <PostalCode
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                {...field}
              />
            )}
          </ValidatedField>
        </div>

        {includeDates && (
          <div className="md:w-40 md:mr-4">
            <ValidatedField
              name={`rfq.${endpointField}.date`}
              label={`${endpointLabel} Date*`}>
              {({ field }) => (
                <DatePicker
                  {...field}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  popperPlacement="bottom-end"
                  includeDates={includeDates}
                />
              )}
            </ValidatedField>
          </div>
        )}
        {originAddresses?.length && endpointField === 'origin' ? (
          <div className="md:mr-4 ">
            <ValidatedField
              name={`rfq.${endpointField}.pastAddress`}
              label={`Optional: Use your past ${endpointLabel} Locations`}>
              {() => (
                <Select placeholder="Select previous address" variant="filled">
                  {originAddresses.map(addr => (
                    <option
                      key={addr.uuid}
                      value={addr.uuid}
                      onClick={() => onSelectAddress(addr, endpointLabel)}>
                      {addr.streetAddress}, {addr.city}, {addr.state},{' '}
                      {addr.postalCode}
                    </option>
                  ))}
                </Select>
              )}
            </ValidatedField>
          </div>
        ) : (
          <></>
        )}
        {destinationAddresses?.length && endpointField === 'destination' ? (
          <div className="md:mr-4 ">
            <ValidatedField
              name={`rfq.${endpointField}.pastAddress`}
              label={`Optional: Use your past ${endpointLabel} Locations`}>
              {() => (
                <Select placeholder="Select previous address" variant="filled">
                  {destinationAddresses.map(addr => (
                    <option
                      key={addr.uuid}
                      value={addr.uuid}
                      onClick={() => onSelectAddress(addr, endpointLabel)}>
                      {addr.streetAddress}, {addr.city}, {addr.state},{' '}
                      {addr.postalCode}
                    </option>
                  ))}
                </Select>
              )}
            </ValidatedField>
          </div>
        ) : (
          <></>
        )}
      </div>

      {isLTL ? (
        <div className="mb-2">
          <ValidatedField
            name={`rfq.${endpointField}.type`}
            label="Location Type*">
            {({ field }) => (
              <LocationTypeSelect
                {...field}
                // @ts-expect-error TODO Couldn't get to some of these in
                // https://plslogistics.atlassian.net/browse/GS-736
                onChange={v => {
                  if (v === 'RESIDENTIAL' || v === 'BUSINESS_WOUT_DOCK') {
                    setFieldValue(
                      `rfq.${endpointField}.accessorials.liftgate`,
                      true,
                      true,
                    );
                  }
                }}
              />
            )}
          </ValidatedField>
        </div>
      ) : (
        <></>
      )}

      {children}
    </div>
  );
};

export function PickupForm() {
  const {
    values: { rfq },
  } = useFormikContext<BookingFlowState>();
  const { origin } = rfq;
  const shipmentType = rfq.shipmentType;
  const isLTL = shipmentType !== 'FTL';
  const includeDates = useMemo(() => getPickupDates(origin), [origin]);

  return (
    <EndpointForm
      endpointLabel="Pickup"
      endpointField="origin"
      includeDates={includeDates}>
      <AdditionalServices
        name="rfq"
        label="Additional Services"
        endpointField="origin"
        endPointAccessorials={
          isLTL ? LTLOriginEndPointAccessorials : FTLOriginEndPointAccessorials
        }
        commonAccessorials={
          isLTL ? undefined : getCommonAccessorial(rfq.truckType)
        }
        temperature={!isLTL && rfq.truckType === 'reefer'}
      />
    </EndpointForm>
  );
}

export function DeliveryForm() {
  const {
    values: { rfq },
  } = useFormikContext<BookingFlowState>();
  const shipmentType = rfq.shipmentType;
  const isLTL = shipmentType !== 'FTL';
  return (
    <EndpointForm endpointLabel="Delivery" endpointField="destination">
      <AdditionalServices
        name="rfq"
        label="Additional Services"
        endpointField="destination"
        endPointAccessorials={
          isLTL
            ? LTLDestinationEndPointAccessorials
            : FTLDestinationEndPointAccessorials
        }
      />
    </EndpointForm>
  );
}
